import { useUploadStore } from "@components/FileUpload/uploadStore";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

export const MAX_MOVEMENT_ATTACHMENTS = 1000;

export interface MovementAttachment {
  originalFileName: string;
  url: string;
  type: string;
}

const getUploadKey = (file: File) =>
  JSON.stringify([file.name, file.lastModified, file.size, file.type]);

export const getUniqueMovementUploadFiles = (files: File[], maxFiles: number) =>
  [...new Map(files.map((file) => [getUploadKey(file), file])).values()].slice(-maxFiles);

export const useMovementAttachmentUploads = (screen: string, fieldName: string) =>
  useMutation({
    mutationFn: ({ files, documentType }: { files: File[]; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen,
          action: "upload_movement_service_document",
          entityType: "movement_service_document",
        },
        async (transactionId) => {
          const store = useUploadStore.getState();
          for (const file of files) {
            // Uploaded entries replace their File; removed files are also absent from the store.
            if (!store.getFilesForKey(fieldName).includes(file)) continue;

            const { blobUrl, sasToken } = await getSasToken(
              {
                prefix: SasTokenPrefix.MOVEMENT_SERVICE,
                contentType: file.type || null,
                privilege: SasTokenPrivilege.WRITE,
                blobUrl: null,
              },
              ...(transactionId ? [transactionId] : []),
            );
            const [result] = await uploadFileToAzureBlob({
              file,
              blobUrl,
              sasToken,
              certificateType: documentType,
            });
            const url = result?.documentUrl ?? blobUrl;
            store.updateFilesForKey(fieldName, (entries) =>
              entries.map((entry) =>
                entry === file ? { name: file.name, type: file.type, url } : entry,
              ),
            );
          }
        },
      ),
  });
